import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Fuse from 'fuse.js';
import { Search } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import { Input } from '@/shadcn/input';
import { cn } from '@/shadcn/lib/utils';

import useInput from '@/utils/hooks/useInput';
import { useWorkflowFavorites } from './useWorkflowFavorites';
import { WorkflowCard } from './WorkflowCard';
import type { WorkflowCardItem } from './workspaceFilterMatch';

const SEARCH_DEBOUNCE_MS = 200;

// Reorder transition for when a card moves because its favourite status changed
// (favouriting bumps it to the front). A spring with NO bounce: an overshoot
// would briefly push a card past the scroll container's edges and flash the
// horizontal/vertical scrollbars, so the motion stays strictly monotonic.
const REORDER_TRANSITION = {
  type: 'spring',
  duration: 0.4,
  bounce: 0
} as const;

// Used for every other layout change (search, workspace filter): collapse the
// reorder to an instant snap so that only favouriting is animated.
const INSTANT_TRANSITION = { duration: 0 } as const;

// Fixed, responsive column count (does not vary with the number of cards).
// Each step is ~512px apart so cards stay roomy: prefer fewer, larger cards and
// only add a column once there is enough width for it.
//   <640px: 1 · sm 640px: 2 · lg 1024px: 3 · 2xl 1536px: 4
const GRID_COLS_CLASS =
  'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4';

const SearchInput = memo(
  ({ onDebouncedChange }: { onDebouncedChange: (value: string) => void }) => {
    const [searchQuery, onSearchChange] = useInput(
      '',
      onDebouncedChange,
      SEARCH_DEBOUNCE_MS
    );
    return (
      <div className="relative flex items-center">
        <Search className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          autoFocus
          className="h-7 w-48 pl-8 text-sm"
          onChange={onSearchChange}
          placeholder="Search workflows…"
          type="search"
          value={searchQuery}
        />
      </div>
    );
  }
);

type Props = {
  workflows: WorkflowCardItem[];
  isPrivileged: boolean;
  userId: string;
  className?: string;
};

export const WorkflowsCards = memo(
  ({ workflows, isPrivileged, userId, className }: Props) => {
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const { isFavorite, toggleFavorite } = useWorkflowFavorites(userId);
    const shouldReduceMotion = useReducedMotion();

    // Only favouriting should animate the reorder; searching/filtering must
    // snap. Flag the next render as favourite-driven, then clear the flag after
    // commit so any later (filter-driven) render falls back to an instant snap.
    const animateReorderRef = useRef(false);
    useEffect(() => {
      animateReorderRef.current = false;
    });

    const handleToggleFavorite = useCallback(
      (rowId: string, value: boolean) => {
        animateReorderRef.current = true;
        toggleFavorite(rowId, value);
      },
      [toggleFavorite]
    );

    const layoutTransition = animateReorderRef.current
      ? REORDER_TRANSITION
      : INSTANT_TRANSITION;

    const onDebouncedChange = useCallback(
      (value: string) => setDebouncedQuery(value),
      []
    );

    // Index the workflows as-is: favourite status doesn't affect searchability,
    // so the index only needs to rebuild when the workflow set itself changes
    // (not on every favourite toggle).
    const fuse = useMemo(
      () =>
        new Fuse(workflows, {
          keys: ['name', 'description'],
          threshold: 0.4
        }),
      [workflows]
    );

    // Search first, then bubble favourites to the front. Toggling a favourite
    // re-sorts without rebuilding the Fuse index.
    const filtered = useMemo(() => {
      const base = debouncedQuery.trim()
        ? fuse.search(debouncedQuery).map(r => r.item)
        : workflows;
      const favorites = base.filter(w => isFavorite(w.rowId));
      const rest = base.filter(w => !isFavorite(w.rowId));
      return [...favorites, ...rest];
    }, [debouncedQuery, fuse, workflows, isFavorite]);

    return (
      <div
        className={cn(
          'flex max-h-[70vh] flex-col flex-nowrap px-4 py-8',
          className
        )}
      >
        <div className="mb-4 flex shrink-0 flex-row items-center justify-between">
          <div className="flex shrink-0 flex-col flex-nowrap font-semibold text-base">
            Your workflows
            <div className="h-1 w-4 rounded-lg bg-secondary" />
          </div>
          <SearchInput onDebouncedChange={onDebouncedChange} />
        </div>

        {filtered.length > 0 ? (
          <div
            className={cn(
              'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20 grid min-h-0 flex-1 gap-3 overflow-y-auto overflow-x-hidden',
              GRID_COLS_CLASS
            )}
            style={{
              scrollbarGutter: 'stable'
            }}
          >
            {filtered.map(workflow => (
              <motion.div
                className="h-full"
                key={workflow.rowId}
                layout={shouldReduceMotion ? false : 'position'}
                transition={layoutTransition}
              >
                <WorkflowCard
                  isFavorite={isFavorite(workflow.rowId)}
                  isPrivileged={isPrivileged}
                  onToggleFavorite={handleToggleFavorite}
                  userId={userId}
                  workflow={workflow}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex h-[86px] w-full items-center justify-center">
            <div className="text-center text-card-foreground text-sm">
              {debouncedQuery.trim() ? 'No matching workflow.' : 'No workflow.'}
            </div>
          </div>
        )}
      </div>
    );
  }
);
